import { useEffect } from 'react';

interface TermsOfServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const sections = [
  {
    icon: 'ri-file-text-line',
    title: 'Acceptance of Terms',
    content: [
      {
        label: 'Agreement to Terms',
        text: 'By accessing or using the website operated by McALLISTER DELIGHT ("Company", "we", "our"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using this site.',
      },
      {
        label: 'Modifications',
        text: 'McALLISTER DELIGHT reserves the right to revise these Terms of Service at any time without notice. By using this website, you agree to be bound by the current version of these Terms of Service. We encourage you to review this page periodically.',
      },
    ],
  },
  {
    icon: 'ri-service-line',
    title: 'Use of Services',
    content: [
      {
        label: 'Permitted Use',
        text: 'This website and its content are provided for the purposes of obtaining information about McALLISTER DELIGHT\'s logistics services, submitting business inquiries, and engaging with our company in a lawful and professional manner.',
      },
      {
        label: 'Prohibited Activities',
        text: 'You may not use this website for any unlawful purpose, to solicit the performance of any illegal activity, to violate any applicable regulations, or to transmit any unsolicited commercial communications. Automated scraping, data harvesting, and attempts to gain unauthorised access to our systems are strictly prohibited.',
      },
      {
        label: 'Service Availability',
        text: 'McALLISTER DELIGHT does not guarantee uninterrupted or error-free access to this website. We reserve the right to modify, suspend, or discontinue any aspect of the website at any time without notice or liability.',
      },
    ],
  },
  {
    icon: 'ri-truck-line',
    title: 'Logistics & Service Agreements',
    content: [
      {
        label: 'Quotations & Proposals',
        text: 'Any quotation or service proposal provided through this website or by our team is valid for 30 days from the date of issue unless otherwise stated, and is subject to final agreement in a formal written contract. Quotes are non-binding until both parties have executed a service agreement.',
      },
      {
        label: 'Client Responsibilities',
        text: 'Clients engaging McALLISTER DELIGHT for food logistics services are responsible for ensuring that all goods tendered for transport or storage comply with applicable Canadian federal and provincial regulations, including CFIA requirements, labelling standards, and temperature specifications.',
      },
      {
        label: 'Liability Limitations',
        text: 'Our liability for loss or damage to goods in transit or storage is governed exclusively by the terms of the applicable service agreement and, where relevant, the applicable tariff or bill of lading. McALLISTER DELIGHT\'s liability shall not exceed the declared or agreed value of the goods.',
      },
    ],
  },
  {
    icon: 'ri-copyright-line',
    title: 'Intellectual Property',
    content: [
      {
        label: 'Ownership',
        text: 'All content on this website — including text, graphics, logos, images, and software — is the property of McALLISTER DELIGHT or its content suppliers and is protected by Canadian and international copyright laws.',
      },
      {
        label: 'Limited Licence',
        text: 'You are granted a limited, non-exclusive, non-transferable licence to access and use this website for personal, non-commercial purposes. This licence does not include the right to reproduce, distribute, modify, create derivative works, publicly display, or commercially exploit any content without prior written consent from McALLISTER DELIGHT.',
      },
      {
        label: 'Trademarks',
        text: 'The McALLISTER DELIGHT name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of the Company. You must not use such marks without our prior written permission.',
      },
    ],
  },
  {
    icon: 'ri-alert-line',
    title: 'Disclaimers & Limitation of Liability',
    content: [
      {
        label: 'No Warranties',
        text: 'This website is provided on an "as is" and "as available" basis. McALLISTER DELIGHT makes no representations or warranties of any kind, express or implied, regarding the operation of the site or the accuracy, completeness, or reliability of the information contained herein.',
      },
      {
        label: 'Limitation of Liability',
        text: 'To the fullest extent permitted by applicable law, McALLISTER DELIGHT shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, this website or its content.',
      },
      {
        label: 'External Links',
        text: 'This website may contain links to third-party websites. McALLISTER DELIGHT has no control over the content of those sites and accepts no responsibility for their content or for any loss or damage that may arise from your use of them.',
      },
    ],
  },
  {
    icon: 'ri-scales-line',
    title: 'Governing Law & Dispute Resolution',
    content: [
      {
        label: 'Governing Law',
        text: 'These Terms of Service are governed by and construed in accordance with the laws of the Province of New Brunswick and the federal laws of Canada applicable therein, without regard to conflict of law provisions.',
      },
      {
        label: 'Dispute Resolution',
        text: 'Any dispute arising out of or in connection with these Terms shall first be submitted to good-faith negotiation between the parties. If unresolved within 30 days, disputes shall be referred to binding arbitration in New Brunswick in accordance with the Arbitration Act (New Brunswick).',
      },
      {
        label: 'Severability',
        text: 'If any provision of these Terms is found to be unenforceable or invalid under applicable law, such provision shall be modified to the minimum extent necessary to make it enforceable, or severed if modification is not possible, and the remaining provisions shall continue in full force and effect.',
      },
    ],
  },
];

export default function TermsOfServiceModal({ isOpen, onClose }: TermsOfServiceModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative h-52 md:h-64 rounded-t-2xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/f17686fc9181624bbaa05552c682c0ff.jpg"
            alt="Terms of Service — McALLISTER DELIGHT"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <p className="text-[#DC2626] text-xs font-bold uppercase tracking-widest mb-2">
              LEGAL & COMPLIANCE
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Terms of Service
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-8 md:p-10">
          {/* Intro */}
          <div className="mb-8 p-5 rounded-xl bg-gray-50 border border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#DC2626]/10 rounded-lg mt-0.5">
                <i className="ri-information-line text-[#DC2626] text-lg"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm leading-relaxed mb-2">
                  These Terms of Service govern your access to and use of the McALLISTER DELIGHT website and describe the terms and conditions that apply to any logistics or cold chain services procured from the Company. Please read these terms carefully before using our services.
                </p>
                <p className="text-gray-500 text-xs">
                  These Terms constitute a legally binding agreement between you and McALLISTER DELIGHT. <strong>Last updated: January 2025.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-8 mb-8">
            {sections.map((section, i) => (
              <div key={i}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#1A365D] rounded-lg">
                    <i className={`${section.icon} text-white text-sm`}></i>
                  </div>
                  <h3 className="text-base font-bold text-[#1A365D]">
                    {i + 1}. {section.title}
                  </h3>
                </div>
                <div className="space-y-3 pl-11">
                  {section.content.map((item, j) => (
                    <div key={j} className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                      <p className="text-xs font-bold text-gray-700 mb-1">{item.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact block */}
          <div className="rounded-2xl bg-[#1A365D] p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-white/15 rounded-xl">
                <i className="ri-customer-service-2-line text-white text-lg"></i>
              </div>
              <div>
                <h4 className="font-bold text-base mb-1">Legal Enquiries</h4>
                <p className="text-gray-300 text-xs leading-relaxed mb-3">
                  If you have questions regarding these Terms of Service or your rights and obligations as a client or visitor, please contact our legal and compliance team.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <i className="ri-building-line text-[#DC2626]"></i>
                    <span>McALLISTER DELIGHT — Legal & Compliance</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <i className="ri-map-pin-line text-[#DC2626]"></i>
                    <span>New Brunswick, Canada</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <p className="text-gray-400 text-[11px] text-center mt-6 leading-relaxed">
            McALLISTER DELIGHT reserves the right to amend these Terms of Service at any time. The most current version will always be available on our website. Your continued use of this website or our services following any update constitutes your acceptance of the revised Terms.
          </p>
        </div>
      </div>
    </div>
  );
}
